import { Chess, SQUARES, type PieceSymbol, type Square } from "chess.js";

export type ChessColor = "w" | "b";

export interface AppliedMove {
  fen: string;
  san: string;
  uci: string;
}

export type ChessRole = "pawn" | "knight" | "bishop" | "rook" | "queen" | "king";

export interface ChessPieceOnSquare {
  square: string;
  color: ChessColor;
  role: ChessRole;
}

export interface PgnTimelineMove extends AppliedMove {
  fenBefore: string;
  comment: string | null;
}

const ROLES: Record<PieceSymbol, ChessRole> = {
  p: "pawn",
  n: "knight",
  b: "bishop",
  r: "rook",
  q: "queen",
  k: "king",
};

const position = (fen: string): Chess | null => {
  try {
    return new Chess(fen);
  } catch {
    return null;
  }
};

export const applyLegalMove = (fen: string, uci: string): AppliedMove | null => {
  const chess = position(fen);
  const match = /^([a-h][1-8])([a-h][1-8])([qrbn])?$/.exec(uci);
  if (!chess || !match) return null;

  try {
    const move = chess.move({ from: match[1], to: match[2], promotion: match[3] });
    return { fen: chess.fen(), san: move.san, uci: `${move.from}${move.to}${move.promotion ?? ""}` };
  } catch {
    return null;
  }
};

export const legalUciMoves = (fen: string): readonly string[] => {
  const chess = position(fen);
  if (!chess) return [];
  return chess.moves({ verbose: true }).map((move) => `${move.from}${move.to}${move.promotion ?? ""}`);
};

export const legalMovesFrom = (fen: string, square: string): readonly string[] => {
  const chess = position(fen);
  if (!chess || !SQUARES.includes(square as Square)) return [];
  return chess
    .moves({ square: square as Square, verbose: true })
    .map((move) => `${move.from}${move.to}${move.promotion ?? ""}`);
};

export const applySanMove = (fen: string, san: string): AppliedMove | null => {
  const chess = position(fen);
  if (!chess) return null;
  try {
    const move = chess.move(san);
    return { fen: chess.fen(), san: move.san, uci: `${move.from}${move.to}${move.promotion ?? ""}` };
  } catch {
    return null;
  }
};

export const sanForUci = (fen: string, uci: string): string | null => applyLegalMove(fen, uci)?.san ?? null;

export const replaySanLine = (fen: string, moves: readonly string[]): readonly AppliedMove[] | null => {
  const replayed: AppliedMove[] = [];
  let current = fen;
  for (const san of moves) {
    const applied = applySanMove(current, san);
    if (!applied) return null;
    replayed.push(applied);
    current = applied.fen;
  }
  return replayed;
};

export const piecesInPosition = (fen: string): readonly ChessPieceOnSquare[] => {
  const chess = position(fen);
  if (!chess) return [];
  return SQUARES.flatMap((square) => {
    const piece = chess.get(square);
    return piece ? [{ square, color: piece.color, role: ROLES[piece.type] }] : [];
  });
};

export const isCheckmate = (fen: string): boolean => position(fen)?.isCheckmate() ?? false;

export const attackedSquares = (fen: string, color: ChessColor): ReadonlySet<string> => {
  const chess = position(fen);
  if (!chess) return new Set();
  return new Set(SQUARES.filter((square) => chess.isAttacked(square, color)));
};

export const parseGamePgn = (
  pgn: string,
): { headers: Readonly<Record<string, string>>; moves: readonly string[] } | null => {
  try {
    const chess = new Chess();
    chess.loadPgn(pgn, { strict: true });
    return { headers: chess.getHeaders(), moves: chess.history() };
  } catch {
    return null;
  }
};

export const parseGamePgnTimeline = (
  pgn: string,
): { headers: Readonly<Record<string, string>>; moves: readonly PgnTimelineMove[] } | null => {
  try {
    const chess = new Chess();
    chess.loadPgn(pgn, { strict: true });
    const comments = new Map(chess.getComments().map(({ fen, comment }) => [fen, comment]));
    return {
      headers: chess.getHeaders(),
      moves: chess.history({ verbose: true }).map((move) => ({
        fenBefore: move.before,
        fen: move.after,
        san: move.san,
        uci: `${move.from}${move.to}${move.promotion ?? ""}`,
        comment: comments.get(move.after) ?? null,
      })),
    };
  } catch {
    return null;
  }
};
